/**
 * What Jackrabbit has told us over the feed (D-816, D-819).
 *
 * **Two triggers change a roster and the rest change nothing**, so the outcome
 * is the column that matters: `Applied` says the studio moved, `Resolved` says
 * we found what it named and left everything alone, and they must never read
 * alike.
 */

import { lastSeen } from './devices'

export type FeedDelivery = {
  id: string
  receivedAt: string
  /** The trigger, as the Zap named it: `student.dropped`. */
  type: string
  /** Zapier's own id for the delivery, where it sends one. */
  deliveryId: string | null
  /** The date the thing happened, in the studio's calendar. */
  eventOn: string | null
  /**
   * How far it got.
   *
   * `Received` · `Resolved` · `Unmatched` · `Unreadable` · `Duplicate` ·
   * `Applied` · `Superseded` · `ApplyFailed` · `NoChangeNeeded` ·
   * `NotActedOn` · `UnknownTrigger`. Every one has
   * words and a colour, and the check is **`FeedOutcomeWordsTests.cs`**, which
   * reads the C# enum itself — vitest cannot see it, so `feed.test.ts` covers
   * only that the words and tones differ where they must. A name with neither
   * falls through to plain grey, which is how "we could not do it" would look
   * like nothing much.
   */
  outcome: string
  /** What was found, or which half was not — in a sentence. */
  note: string | null
  dancerId: string | null
  studioClassId: string | null
  /**
   * Whoever the delivery was about, by name (D-831).
   *
   * **The ids above have been here since the first delivery and this screen
   * read neither**, so a row said *"Taken off the roster, dated 22 Sep 2026"*
   * with nobody on it — and after thirty days the raw body is gone too (D-820),
   * so the row went anonymous exactly when somebody came back to ask what the
   * feed did in September. The server resolves the name live, because a name
   * copied at delivery time goes stale the first time somebody is renamed.
   */
  who: string | null
  /**
   * The body exactly as it arrived, while we still hold it.
   *
   * **Shown rather than summarised**, because nobody knew what Jackrabbit
   * actually sends until this screen existed — and a summary of a shape nobody
   * has seen is a summary of a guess.
   */
  body: string
  /**
   * When the retention sweep dropped the payload, if it has (D-820).
   *
   * **The server decides this, not us.** An empty body means either *dropped
   * after a month* or *arrived carrying nothing*, and both are reachable — a
   * POST with no body is recorded like any other delivery. Guessing from the
   * emptiness alone would make the screen state one as the other.
   */
  bodyForgottenAt: string | null
}

/**
 * One trigger, and when we last heard from it (D-823).
 *
 * **The row that matters is the one with `lastReceivedAt: null`** — a Zap that
 * has never spoken. That is defined by an absence, so a list of deliveries can
 * never show it, and it is exactly what somebody building ten Zaps one at a time
 * needs to see.
 */
export type FeedTrigger = {
  type: string
  lastReceivedAt: string | null
  count: number
  /** False for a name that is not one of the ten — a mis-typed Zap. */
  recognised: boolean
  /** Whether a delivery under this trigger changes a roster. */
  acts: boolean
}

/**
 * **Both optional, because both are read behind a `??`.**
 *
 * A new bundle can meet an API started before either field existed - the
 * ordinary state of a dev machine with a stale `dotnet run` - so the screen
 * guards for them. Typed as required, the guard was unreachable by the type
 * while being necessary at runtime, and the comment explaining it described
 * something TypeScript said could not happen.
 */
export type FeedActivity = { deliveries?: FeedDelivery[]; triggers?: FeedTrigger[] }

/**
 * "Heard 4 min ago", or "Never".
 *
 * **Delegates to `lastSeen`** rather than restating four branches of time
 * arithmetic — it answers the same question about a classroom tablet, and two
 * copies of one idea drift with the second never receiving the first's fixes.
 * Only the empty case differs: *Never used* is right about a device somebody
 * could have picked up, and wrong about a Zap that may simply not exist yet.
 */
export function lastHeard(at: string | null, now: Date = new Date()): string {
  return at ? lastSeen(at, now) : 'Never'
}

export const FEED_KEY = ['jackrabbit-feed'] as const

export async function fetchFeedActivity(): Promise<FeedActivity> {
  const res = await fetch('/api/integrations/jackrabbit/activity')
  if (!res.ok) throw new Error(`Could not load what the feed has received (${res.status})`)
  return res.json()
}

/**
 * How each outcome should read.
 *
 * **`Matched` and `Applied` are the pair to keep apart.** One means we found the
 * dancer and the class and changed nothing; the other means a roster moved. A
 * screen that blurred them would answer "did anything happen?" wrongly in both
 * directions.
 */
export const OUTCOME_WORDS: Record<string, string> = {
  Resolved: 'Matched',
  Unmatched: 'Nothing matched',
  Unreadable: 'Could not be read',
  Received: 'Received',
  Duplicate: 'Sent before',
  Applied: 'Applied',
  Superseded: 'Out of date',
  NoChangeNeeded: 'Already so',
  NotActedOn: 'Recorded only',
  UnknownTrigger: 'Trigger not recognised',
  ApplyFailed: 'Could not be applied',
  HeldBack: 'Held, too many at once',
}

export function outcomeWords(outcome: string): string {
  return OUTCOME_WORDS[outcome] ?? outcome
}

/**
 * The outcome as a colour as well as a word.
 *
 * **`good` / `crit` / `warn`, never `ok` / `bad`** — those tokens have never
 * existed, and a missing Tailwind utility renders as no colour at all rather
 * than as an error. `MarkButtons` carries the same warning, because the
 * most-tapped chip on the most-used screen shipped colourless that way.
 *
 * **Here rather than in the component**, so one test can bind both halves of a
 * name to the enum that produces it.
 */
export const OUTCOME_TONES: Record<string, string> = {
  // The studio moved. The one outcome worth reading as an event.
  Applied: 'bg-good-bg text-good border-good',

  // Found, and nothing needed doing. Deliberately quiet: the studio already
  // agreed, which is the commonest thing a replayed delivery has to say.
  Resolved: 'bg-surface-2 text-ink-2 border-line-2',
  Received: 'bg-surface-2 text-ink-2 border-line-2',
  Duplicate: 'bg-surface-2 text-ink-2 border-line-2',
  NoChangeNeeded: 'bg-surface-2 text-ink-2 border-line-2',

  /*
   * Grey, because a trigger nothing acts on yet is an ordinary state rather
   * than a failure (D-821) — amber here would cost the one amber that matters,
   * `Unmatched`. Which triggers those are is `FeedApply.ActsOn`'s to say, never
   * a count written here (D-833).
   */
  NotActedOn: 'bg-surface-2 text-ink-2 border-line-2',

  /*
   * Refused on purpose, and grey rather than amber: an out-of-order delivery
   * correctly ignored is the ordering guard *succeeding*. Amber on a routine
   * correct outcome trains the eye past amber, and the one amber that matters
   * is below it. The words 'Out of date' carry the meaning without the colour.
   */
  Superseded: 'bg-surface-2 text-ink-2 border-line-2',

  /*
   * The two that need a person. Amber is reserved for these, which is the whole
   * reason `Superseded` and `NotActedOn` are grey — six routine states wearing
   * the colour that means "act" is how amber stops meaning anything.
   */
  Unmatched: 'bg-warn-bg text-warn border-warn',

  // And this one is fixable in thirty seconds, in Zapier (D-822).
  UnknownTrigger: 'bg-warn-bg text-warn border-warn',

  // We could not read it, or we meant to act and could not.
  Unreadable: 'bg-crit-bg text-crit border-crit',
  ApplyFailed: 'bg-crit-bg text-crit border-crit',

  /*
   * A working safety catch, not a fault (D-829). Amber because somebody may
   * want to look — a held delivery means something changed a lot at once — and
   * never red, because nothing is broken and Jackrabbit is already sending it
   * again. Red here would send a reader hunting a failed write that does not
   * exist, which is the mistake D-821 and D-822 each fixed once.
   */
  HeldBack: 'bg-warn-bg text-warn border-warn',
}

export function outcomeTone(outcome: string): string {
  return OUTCOME_TONES[outcome] ?? 'bg-surface-2 text-ink-2 border-line-2'
}

/**
 * **Pretty-printed where it is JSON, left alone where it is not.**
 *
 * A body that is not JSON is the interesting case — it is why the delivery was
 * recorded `Could not be read` — so mangling it would hide the evidence.
 */
export function readableBody(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body), null, 2)
  } catch {
    return body
  }
}

/**
 * What there is to show for a delivery, in three states rather than two.
 *
 * **An empty body is not one thing.** The row is kept for ever and the payload
 * is dropped after a month, so an old delivery with nothing to show is the rule
 * working — but a delivery that arrived carrying nothing is also empty, and that
 * is reachable: a POST with no body parses as unreadable and is recorded like
 * any other. Rendered as an empty box the first reads as data loss; described as
 * *forgotten* the second is a confident false statement. Both are D-209 pointed
 * at the record itself, which is why the server marks the row and this only
 * reads the mark.
 */
export type PayloadState = 'held' | 'forgotten' | 'never-arrived'

export function payloadState(row: FeedDelivery): PayloadState {
  if (row.body.length > 0) return 'held'

  return row.bodyForgottenAt ? 'forgotten' : 'never-arrived'
}
